import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatErr } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauth, object = auth
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      return { ok: true };
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Structured 403 from backend: { code: "email_not_verified", message, email }
      if (detail && typeof detail === "object" && detail.code === "email_not_verified") {
        return { ok: false, code: "email_not_verified", email: detail.email, error: detail.message };
      }
      // Real backend error with a string detail (e.g. 401 "Invalid email or password")
      if (detail) return { ok: false, error: formatErr(detail) };
      // Backend reachable but returned no body (rare)
      if (e.response) {
        const code = e.response.status;
        if (code === 502 || code === 503 || code === 504) {
          return { ok: false, error: "Server is starting up or temporarily busy. Please try again in a few seconds." };
        }
        return { ok: false, error: `Sign in failed (HTTP ${code}). Please try again.` };
      }
      // No response at all — network failure / CORS / DNS / offline
      return { ok: false, error: "Could not reach the server. Check your internet connection and try again." };
    }
  };

  const register = async ({ email, password, confirmPassword, name, companyName,
                              phone, industry, subIndustries, isAdmin }) => {
    try {
      const { data } = await api.post("/auth/register", {
        email,
        password,
        confirm_password: confirmPassword,
        name,
        company_name: companyName,
        phone: phone || null,
        industry,
        sub_industries: subIndustries || [],
        is_admin: !!isAdmin,
      });
      return { ok: true, pending: true, email: data.email, message: data.message };
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Special case: account was created but the email transport failed
      if (e.response?.status === 502 && typeof detail === "string" && detail.includes("Resend")) {
        return {
          ok: false,
          created: true,
          email,
          error: detail,
        };
      }
      return { ok: false, error: formatErr(detail) || e.message };
    }
  };

  const resendVerification = async (email) => {
    try {
      const { data } = await api.post("/auth/resend-verification", { email });
      return { ok: true, message: data.message };
    } catch (e) {
      return { ok: false, error: formatErr(e.response?.data?.detail) || e.message };
    }
  };

  const forgotPassword = async (email) => {
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      return { ok: true, message: data.message };
    } catch (e) {
      return { ok: false, error: formatErr(e.response?.data?.detail) || e.message };
    }
  };

  const resetPassword = async ({ token, newPassword, confirmPassword }) => {
    try {
      const { data } = await api.post("/auth/reset-password", {
        token,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      // Backend clears cookies — drop our cached user too.
      setUser(false);
      return { ok: true, message: data.message };
    } catch (e) {
      return { ok: false, error: formatErr(e.response?.data?.detail) || e.message };
    }
  };

  const changePassword = async ({ currentPassword, newPassword, confirmPassword }) => {
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      return { ok: true, message: data.message };
    } catch (e) {
      return { ok: false, error: formatErr(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(false);
  };

  return (
    <AuthCtx.Provider value={{ user, ready, login, register, logout, refresh, resendVerification, forgotPassword, resetPassword, changePassword }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
